export function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/**
 * startOfWeek — normalize date to the user-preferred first day of week.
 * v150 Phase 4 — generalized from startOfWeekMon to accept 'mon' | 'sun'.
 * startOfWeekMon remains as a backward-compat alias for callers that
 * haven't migrated to useFirstDayPreference yet.
 */
export function startOfWeek(d: Date, firstDay: "mon" | "sun" = "mon"): Date {
  const x = new Date(d);
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = firstDay === "mon" ? (day + 6) % 7 : day;
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function startOfWeekMon(d: Date): Date {
  return startOfWeek(d, "mon");
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function fmtRange(start: Date, end: Date, locale = "en"): string {
  const same = start.getMonth() === end.getMonth();
  const m1 = start.toLocaleDateString(locale, { month: "short" });
  const m2 = end.toLocaleDateString(locale, { month: "short" });
  if (same) return `${start.getDate()}–${end.getDate()} ${m2}`;
  return `${start.getDate()} ${m1} – ${end.getDate()} ${m2}`;
}

export function dayLabel(d: Date, locale = "en"): string {
  return d.toLocaleDateString(locale, { weekday: "short" });
}

/**
 * daysUntilIso — календарная разница в днях между сегодня и ISO-датой.
 * Не зависит от времени суток (полночь-нормализация обеих дат) —
 * «через N дн» обязан быть одинаковым в 09:00 и в 23:00 и совпадать
 * во всех табах (инцидент: Путь показывал 18, План — 17).
 */
export function daysUntilIso(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const target = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/**
 * fmtDateHuman — "2026-06-29" → локализованная дата («29 июня 2026 г.»).
 * Для user-facing поверхностей: сырые ISO-даты в UI запрещены.
 */
export function fmtDateHuman(iso: string, locale = "ru"): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
